import BigNumber from 'bignumber.js';
import {
  IProvidersMap,
  ProviderManager,
  Web3KeyWriteProvider,
} from '@ankr.com/provider';

import {
  EBlockchain,
  IDepositStablecoinToPAYGParams,
  IDepositStablecoinToPAYGForUserParams,
  IGetAllowanceFeeParams,
  IGetDepositStablecoinToPAYGFeeParams,
  ISetAllowanceParams,
  PrefixedHex,
  Web3Address,
  IGetAllowanceValueParams,
} from '../../common';
import { UsdtPAYGContractManager } from '../../PAYGContract';
import { UsdtContractReadService } from './UsdtContractReadService';
import { convertNumberWithDecimalsToString } from '../../utils';

export class USDTContractService extends UsdtContractReadService {
  constructor(
    private readonly keyProvider: Web3KeyWriteProvider,
    protected readonly usdtPAYGContractManager: UsdtPAYGContractManager,
  ) {
    super(usdtPAYGContractManager);
  }

  depositUSDTToPAYG({
    amount,
    depositContractAddress,
    network,
    providerManager,
    tokenAddress,
    tokenDecimals,
  }: IDepositStablecoinToPAYGParams) {
    const formattedAmount = new BigNumber(
      convertNumberWithDecimalsToString(amount, tokenDecimals),
    );

    return this.usdtPAYGContractManager.depositUSDT({
      amount: formattedAmount,
      depositContractAddress,
      network,
      providerManager,
      tokenAddress,
      tokenDecimals,
    });
  }

  getDepositUSDTToPAYGFee({
    amount,
    depositContractAddress,
    network,
    providerManager,
    tokenAddress,
    tokenDecimals,
  }: IGetDepositStablecoinToPAYGFeeParams) {
    return this.usdtPAYGContractManager.getDepositUsdtFee({
      amount,
      depositContractAddress,
      network,
      providerManager,
      tokenAddress,
      tokenDecimals,
    });
  }

  depositUSDTToPAYGForUser({
    amount,
    depositContractAddress,
    network,
    providerManager,
    targetAddress,
    tokenAddress,
    tokenDecimals,
  }: IDepositStablecoinToPAYGForUserParams) {
    const formattedAmount = new BigNumber(
      convertNumberWithDecimalsToString(amount, tokenDecimals),
    );

    return this.usdtPAYGContractManager.depositUsdtForUser({
      depositContractAddress,
      depositValue: formattedAmount,
      network,
      providerManager,
      targetAddress,
      tokenAddress,
      tokenDecimals,
    });
  }

  setAllowanceForPAYG(params: ISetAllowanceParams) {
    return this.usdtPAYGContractManager.setAllowance(params);
  }

  getAllowanceFee({
    amount,
    depositContractAddress,
    network,
    providerManager,
    tokenAddress,
    tokenDecimals,
  }: IGetAllowanceFeeParams) {
    return this.usdtPAYGContractManager.getAllowanceFee({
      amount,
      depositContractAddress,
      network,
      providerManager,
      tokenAddress,
      tokenDecimals,
    });
  }

  getTransactionReceipt(txHash: PrefixedHex) {
    return this.keyProvider.getWeb3().eth.getTransactionReceipt(txHash);
  }

  getAllowanceValue({
    depositContractAddress,
    network,
    providerManager,
    tokenAddress,
  }: IGetAllowanceValueParams) {
    return this.usdtPAYGContractManager.getAllowanceValue({
      depositContractAddress,
      network,
      providerManager,
      tokenAddress,
    });
  }

  async getLatestAllowanceEvent(user: Web3Address) {
    const events = await this.usdtPAYGContractManager.getLatestAllowanceEvents(
      user,
    );

    if (!events?.length) return undefined;

    return events[events.length - 1];
  }

  getCurrentAccountBalance(
    network: EBlockchain,
    providerManager: ProviderManager<IProvidersMap>,
  ) {
    return this.usdtPAYGContractManager.getCurrentAccountBalance(
      network,
      providerManager,
    );
  }
}
